import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEditCoorgEvent } from './useEditAdminEvent';
import { Form, FormGrid, OccupyFreeSpace, SaveButton, TabPanel, TabsHeader, TitleLink, Tooltip, useSaveable, useTabsState } from '../../../toolympus/components/primitives';
import { Typography } from '@mui/material';
import { useItemWithControls } from '../../../toolympus/api/useWithControls';
import { PowerEditorBase } from '../../../toolympus/components/PowerDoc';
import { Locales, LocaleSwitch } from '../../../toolympus/components/LocalizedRecords';
import { MediafileEdit } from '../../../toolympus/components/schemed/MediafileEdit';
import { MediaLibProvider } from '../../../toolympus/components/medialib';
import { FocusControlsPanel, getTranslationField } from './EventAdminPage';
import { DateAndTimeEdit } from '../../../toolympus/components/schemed/TimeEdit';
import { CMSEditorUnwrapped } from '../../CMS';
import { PowerEditorSettingsProvider } from '../../../toolympus/components/PowerDoc/PowerEditorSettingsContext';
import { EventRegistrationsList } from './EventRegistrationsList';
import { SimpleMultiAttributeEditor } from '../../../toolympus/components/primitives/SimpleMultiAttributeEditor';
import { InfoOutlined } from '@mui/icons-material';
import { EventDiscountCodesList } from './EventDiscountCodesList';

interface Props {
  
}


export const EventCoorgPage = (props: Props) => {
  const { id } = useParams<{ id: string }>();
  const data = useEditCoorgEvent(+id);

  const { controls } = useItemWithControls(data, { schema: data.schema });
  // const user = useUser();

  useSaveable(data);

  const [lang,setLang] = useState<string>("en");

  
  const tabs = useTabsState([
    ["attributes","Attributes"],
    ["content","Content"],
    ["registrations","Registrations"],
    ["discount_codes","Discount Codes"],
  ], "attributes", "tab");


  return (
    <MediaLibProvider apiPath="/api/media" spaceId={"riac_event_media"} urlPrefix="/media">
      <Form
        title={<TitleLink to="/my-events">Event</TitleLink>}
        headerItems={<>
          <OccupyFreeSpace />
          {data.hasChanges && <SaveButton action={() => data.save()} />}

          <LocaleSwitch availableLocales={Locales} locale={lang} setLocale={setLang} />
        </>}
        >
        <FormGrid columns="1fr max-content max-content" style={{ alignItems: "flex-end" }}>
          {controls([
            [getTranslationField("title", lang)],
            ["is_published", { disabled: true }],
          ])}

          <Tooltip text="Please contact RIAC organizers to publish / unpublish the event">
            <InfoOutlined fontSize="small" color="primary" sx={{ alignSelf: "center"}} />
          </Tooltip>

          
        </FormGrid>

        <TabsHeader state={tabs} />

        <TabPanel state={tabs.forTab("attributes")}>
          <FormGrid columns="2fr 1fr" forceEvenColumns>
            <FormGrid columns="1fr" noMargin>
              <FormGrid columns="1fr 1fr 1fr" forceEvenColumns noMargin>
                <DateAndTimeEdit
                  field="start_datetime"
                  row={data.data}
                  onChange={(o,c) => data.update(c)}
                  schema={data.schema.start_datetime}
                  dateSchema={{ label: "Date"}}
                  errors={data.errors}
                  extraProps={{ readOnly: true }}
                  />
                <DateAndTimeEdit
                  field="end_datetime"
                  row={data.data}
                  onChange={(o,c) => data.update(c)}
                  schema={data.schema.end_datetime}
                  noDate
                  errors={data.errors}
                  extraProps={{ readOnly: true }}
                  />
              </FormGrid>

              

              <FormGrid columns="1fr 1fr" forceEvenColumns noMargin alignItems="end">
                {controls([
                  [getTranslationField("location", lang)],
                  ["location_url"],
                  ["event_format"],
                  ["languages"],
                ])}
              </FormGrid>

              <SimpleMultiAttributeEditor
                label="Organiser companies"
                value={lang === "ru" ? data.data.organiser_companies_ru : data.data.organiser_companies}
                fields={[
                  ["title", { controlProps: { autoFocus: true }}],
                  ["url"],
                ]}
                displayText={o => o.title}
                schema={{
                  title: { label: "Organiser"},
                  url: { label: "URL"},
                }}
                update={v => lang === "ru" ? data.update({ organiser_companies_ru: v }) : data.update({ organiser_companies: v })}
                />

              <FormGrid columns="repeat(3,1fr)">
                {controls([
                  ["created_at", { disabled: true, utcToLocal: true }],
                  ["updated_at", { disabled: true, utcToLocal: true }],
                  ["published_at", { disabled: true, utcToLocal: true }],
                  ["created_by", { disabled: true }],
                  ["updated_by", { disabled: true }],
                  ["published_by", { disabled: true }],
                ])}
              </FormGrid>
            </FormGrid>

            <FormGrid columns="1fr" noMargin>
              <FocusControlsPanel columns="1fr" noMargin>
                {controls([
                  ["registration_status"],
                  data.data.registration_status === "external_link"
                    ? [getTranslationField("registration_external_url", lang)]
                    : null,
                  ["price_option"],
                ])}

                {data.data.price_option === "paid" && <>
                  <FormGrid columns="1fr 1fr" noMargin forceEvenColumns>
                    {controls([
                      [getTranslationField("price", lang)],
                      [getTranslationField("price_currency", lang)],
                    ])}
                  </FormGrid>

                  {controls([
                      ["allow_free_requests"],
                    ])}
                </>}

              </FocusControlsPanel>

              <MediafileEdit
                field="cover_image"
                row={data.data}
                onChange={(o,c) => data.update(c)}
                schema={data.schema["cover_image"]}
                />
            </FormGrid>
          </FormGrid>



          
        </TabPanel>

        <TabPanel state={tabs.forTab("content")}>
          <Typography variant="caption" color="textSecondary">Description (short)</Typography>
          {lang === "ru"
            ? <PowerEditorBase
                key={`${data.data._id}_dsru_${data.loadBumper || ""}`}
                content={data.data.description_short_ru}
                update={v => data.update({ description_short_ru: v })}
                editorElementId="doc-editor"
                />
            : <PowerEditorBase
                key={`${data.data._id}_ds_${data.loadBumper || ""}`}
                content={data.data.description_short}
                update={v => data.update({ description_short: v })}
                editorElementId="doc-editor"
                />}
          

          <Typography variant="caption" color="textSecondary">Description (full)</Typography>

          <PowerEditorSettingsProvider
            EditorComponent={CMSEditorUnwrapped}>
            {lang === "ru"
              ? <CMSEditorUnwrapped
                  key={`${data.data._id}_dfru_${data.loadBumper || ""}`}
                  content={data.data.description_ru}
                  update={v => data.update({ description_ru: v })}
                  editorElementId="doc-editor"
                  />
              : <CMSEditorUnwrapped
                  key={`${data.data._id}_df_${data.loadBumper || ""}`}
                  content={data.data.description}
                  update={v => data.update({ description: v })}
                  editorElementId="doc-editor"
                  />}
            </PowerEditorSettingsProvider>
        </TabPanel>


        <TabPanel state={tabs.forTab("registrations")}>
          <EventRegistrationsList variant="coorg" event={data.data} isPaid={data.data.price_option === "paid"} />
        </TabPanel>

        <TabPanel state={tabs.forTab("discount_codes")}>
          <EventDiscountCodesList kind="coorg" eventId={data.data._id} />
        </TabPanel>

      </Form>
    </MediaLibProvider>
  );
}


